import { Component, Inject, OnInit, ChangeDetectorRef } from '@angular/core';

import { MatDialogModule, MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { DragDropModule } from '@angular/cdk/drag-drop';

import { InteractionsService, AdminInteraction } from '../../../../services/interactions.service';
import { ExtendedLogsTableComponent } from '../../../admin/extended-logs/extended-logs-table/extended-logs-table.component';

export interface StatsHistoryDialogData {
  userId: number;
  colleagueName: string;
  month: string;
  year: number;
}

@Component({
  selector: 'app-stats-history-dialog',
  standalone: true,
  imports: [
    MatDialogModule,
    MatButtonModule,
    MatIconModule,
    MatProgressSpinnerModule,
    DragDropModule,
    ExtendedLogsTableComponent
],
  templateUrl: './stats-history-dialog.component.html',
  styleUrl: './stats-history-dialog.component.scss'
})
export class StatsHistoryDialogComponent implements OnInit {
  // Megjelenített logok
  paginatedLogs: AdminInteraction[] = [];
  loading = true; // Alapértelmezetten betöltés állapot, hogy ne változzon ngOnInit-ban
  error: string | null = null;
  totalLogs = 0;

  // Magyar hónapnevek → hónapszám mapping
  private readonly HUNGARIAN_MONTHS = [
    'január', 'február', 'március', 'április', 'május', 'június',
    'július', 'augusztus', 'szeptember', 'október', 'november', 'december'
  ];

  constructor(
    private interactionsService: InteractionsService,
    private dialogRef: MatDialogRef<StatsHistoryDialogComponent>,
    private cdr: ChangeDetectorRef,
    @Inject(MAT_DIALOG_DATA) public data: StatsHistoryDialogData
  ) {}

  ngOnInit(): void {
    this.loadLogs();
  }

  /**
   * ISO 8601 hét szám kiszámítása az adott dátumra.
   * Pl. 2026-05-28 → 22 (a 22. ISO hét, ami júniusba nyúlik)
   */
  private getISOWeek(date: Date): number {
    const target = new Date(date);
    const dayOfWeek = (target.getDay() + 6) % 7; // Hétfő = 0
    target.setDate(target.getDate() - dayOfWeek + 3); // A hét csütörtökjére ugrik
    const firstThursday = target.valueOf();
    target.setMonth(0, 1);
    if (target.getDay() !== 4) {
      target.setMonth(0, 1 + ((4 - target.getDay()) + 7) % 7);
    }
    return 1 + Math.ceil((firstThursday - target.valueOf()) / 604800000);
  }

  /**
   * ISO 8601 naptár szerinti év kiszámítása.
   * December végén / január elején eltérhet a naptári évtől.
   */
  private getISOYear(date: Date): number {
    const target = new Date(date);
    const dayOfWeek = (target.getDay() + 6) % 7;
    target.setDate(target.getDate() - dayOfWeek + 3);
    return target.getFullYear();
  }

  /**
   * Az adott hónaphoz tartozó összes ISO hét mintát adja vissza.
   * Formátum: "{isoÉv}. év {isoHét}. hét" – pontosan így tárolja a backend.
   * Pl. június 2026 → ["2026. év 23. hét", "2026. év 24. hét", ...]
   * Kezeli az ISO év határokat is (pl. december vége / január eleje).
   */
  private getWeekPatternsForMonth(): string[] {
    const monthIndex = this.HUNGARIAN_MONTHS.indexOf(this.data.month.toLowerCase());
    const monthNum = monthIndex === -1 ? 1 : monthIndex + 1;
    const year = this.data.year;
    const daysInMonth = new Date(year, monthNum, 0).getDate();

    const seen = new Set<string>();
    const patterns: string[] = [];

    for (let day = 1; day <= daysInMonth; day++) {
      const date = new Date(year, monthNum - 1, day);
      const isoWeek = this.getISOWeek(date);
      const isoYear = this.getISOYear(date);
      const pattern = `${isoYear}. év ${isoWeek}. hét`;
      if (!seen.has(pattern)) {
        seen.add(pattern);
        patterns.push(pattern);
      }
    }

    return patterns;
  }

  /** Logok betöltése az API-ból, majd szűrés a hónaphoz tartozó ISO hetek alapján */
  loadLogs(): void {
    this.loading = true;
    this.error = null;

    const weekPatterns = this.getWeekPatternsForMonth();

    this.interactionsService.getAllInteractions({
      page: 0,
      pageSize: 1000,
      status: ['Tervszám módosítás', 'Tényszám módosítás'],
      targetUserId: this.data.userId
      // dateFrom / dateTo szándékosan nincs: a szűrés a notes mező
      // alapján történik (melyik héthez tartozik az adat), nem a
      // log bejegyzés létrehozásának dátuma alapján
    }).subscribe({
      next: (response) => {
        // Csak azokat a logokat tartjuk meg, amelyek notes mezője
        // egyezik a hónap valamely ISO hét mintájával
        const filtered = response.items.filter(log =>
          weekPatterns.some(pattern => log.notes === pattern)
        );
        this.paginatedLogs = filtered;
        this.totalLogs = filtered.length;
        this.loading = false;
        this.cdr.detectChanges();
      },
      error: () => {
        this.error = 'Nem sikerült betölteni a naplóbejegyzéseket.';
        this.loading = false;
        this.cdr.detectChanges();
      }
    });
  }

  onClose(): void {
    this.dialogRef.close();
  }
}
